/* eslint-disable react/prop-types */
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { MdOutlinePlaylistPlay } from "react-icons/md";
import { useFontStyle } from "~/hook/useFontStyle";
import { getYoutubeVideoThumbnail } from "~/lib/helperFuncation/youtubeThumbnails";
import { cn } from "~/lib/utils";
import { BLUR_HASH, COLORS, FALLBACK_IMAGE_ALT_TEXT, FALLBACK_IMAGE_URL } from "~/utils/constant";
import { convertSecondsToHMS, mediaAudioOnly } from "~/utils/helper-funcations";
import { PropsType } from "../audio-video/VideoCard";
import AudioIcon from "../shared-components/AudioIcon";
import ClientOnly from "../shared-components/ClentOnly";
import ImageWithSkeleton from "../shared-components/ImageWithSkeleton";
import Tag from "../shared-components/Tag";
import { DISPLAY_TYPE } from "../video-grid/video-data";
import CardShadow from "./CardShadow";

function VerticalCard({ className, data, isChildrenCard = false }: PropsType) {
  const isPlaylist = data?.node_type ? data?.node_type !== DISPLAY_TYPE.branch : false;
  const isBranch = data?.node_type ? data?.node_type === DISPLAY_TYPE.branch : false;
  const isAudioOnly = mediaAudioOnly(data?.media_type!);
  const { normalInter } = useFontStyle();
  const { t } = useTranslation();

  const ImageCard = () => (
    <ImageWithSkeleton
      url={{
        small:
          data?.vertical_img?.s?.url ||
          data?.img?.s?.url ||
          (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "low")) ||
          FALLBACK_IMAGE_URL,
        medium:
          data?.vertical_img?.m?.url ||
          data?.img?.m?.url ||
          (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "medium")) ||
          FALLBACK_IMAGE_URL,
        large:
          data?.vertical_img?.l?.url ||
          data?.img?.l?.url ||
          (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "medium")) ||
          FALLBACK_IMAGE_URL,
        hd:
          data?.vertical_img?.hd?.url ||
          data?.img?.hd?.url ||
          (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "high")) ||
          FALLBACK_IMAGE_URL,
        original:
          data?.vertical_img?.og?.url ||
          data?.img?.og?.url ||
          (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "maximum")) ||
          FALLBACK_IMAGE_URL,
      }}
      alt={data?.name ?? FALLBACK_IMAGE_ALT_TEXT}
      className="w-full h-full object-cover"
      roundedClass="rounded-xl"
      blur_hash={data?.img?.bh || BLUR_HASH}
      isPreloaded={true}
    />
  );

  return (
    <div className={`w-full flex flex-col gap-1 justify-center items-center cursor-pointer relative group ${className}`}>
      {isPlaylist && <CardShadow bgImage={data?.img?.og?.url || FALLBACK_IMAGE_URL} isChildren={isChildrenCard} />}
      <div
        className={cn(
          "aspect-[2/3] relative overflow-hidden z-10 rounded-xl bg-gray-300",
          isChildrenCard ? "xs:h-[130px] smd:h-[150px] sml:h-[175px] md:h-[50%]" : "xs:h-[140px] smd:h-[160px] sml:h-[180px] md:h-[50%] ",
        )}
      >
        <ClientOnly fallback={<ImageCard />}>
          <ImageCard />
        </ClientOnly>

        {data?.tag && <div className="w-auto absolute top-2 right-2">{<Tag text={data?.tag} />}</div>}

        {isAudioOnly && <AudioIcon isVisible={isAudioOnly} className="absolute bottom-2 left-2 z-20" />}

        {/* Playlist Tag */}
        {isPlaylist ? (
          <div className="absolute  bottom-1.5 right-1.5 md:bottom-2 md:right-2 flex items-center  bg-slate-800/70 text-white text-10 md:text-xs px-1 md:px-1 py-0.5 md:py-[3px] rounded-sm z-20 ">
            <MdOutlinePlaylistPlay color={COLORS.white} className="w-4 h-4 md:w-4.5 md:h-4.5" />
            <span className={cn(normalInter)}>
              {data?.part} {t(`common.videos`, { defaultValue: "Videos" })}
            </span>
          </div>
        ) : data?.duration_sec ? (
          <div className="absolute bottom-1.5 right-1.5 md:bottom-2 md:right-2 flex items-center justify-center bg-slate-800/70 text-white text-10 md:text-xs px-1 md:px-1 py-0.5 md:py-[3px] rounded-sm z-10 xs:min-w-[40px] md:min-w-[40px]">
            <span className={cn(normalInter)}>{convertSecondsToHMS(data?.duration_sec || 0)}</span>
          </div>
        ) : isBranch ? (
          <div className="absolute  bottom-1.5 right-1.5 md:bottom-2 md:right-2 flex items-center  bg-slate-800/70 text-white text-10 md:text-xs px-1 md:px-1 py-0.5 md:py-[3px] rounded-sm z-20 ">
            <MdOutlinePlaylistPlay color={COLORS.white} className="w-4 h-4 md:w-4.5 md:h-4.5" />
            <span className={cn(normalInter)}>
              {data?.playlist_item_count} {t(`common.playlist`, { defaultValue: "Playlist" })}
            </span>
          </div>
        ) : null}
      </div>

      {/* Video Details */}
      <div className="w-full flex flex-col justify-start items-start px-3  md:px-0 ">
        <p
          className={cn(
            "common-text-size text-textColorPrimary line-clamp-2 leading-5 overflow-hidden group-hover:text-darkOrangeColor/80 transition-colors duration-200 ease-in-out w-full",
            normalInter,
          )}
          title={data?.name}
        >
          {data?.name || ""}
        </p>

        {(data?.duration_sec || data?.record_date) && (
          <div className="flex justify-start items-center gap-2">
            {data?.record_date && (
              <p className={cn("body_text_xs text-textSecondaryColor", normalInter)}>{dayjs(data?.record_date).format("DD MMM YYYY")}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default VerticalCard;
